'use client';

import { Anchor } from '@/components/ui/anchor';
import { Button } from '@/components/ui/button';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

type TierBenefit = {
  text: React.ReactNode;
};

type TierCardProps = {
  actionHref: string;
  actionLabel: string;
  benefits: TierBenefit[];
  imageAlt: string;
  imageSrc: string;
  starSrc: string;
  tier: 'movie-club' | 'movie-fan';
  title: string;
};

const movieFanBenefits: TierBenefit[] = [
  { text: <strong>FREE!</strong> },
  { text: 'Earn 1 Point for Every $1 Spent' },
  { text: 'Redeem Points for Tickets and More Rewards' },
  { text: 'Member Access to Screenings and Advance Tickets' },
  { text: 'Free Birthday Rewards' },
  { text: 'Exclusive Member Offers' },
  { text: 'Discount Tuesday savings for even more value.' },
  { text: 'Enjoy free refills on large popcorn and drinks (same day of purchase; one per item).' },
];

const movieClubBenefits: TierBenefit[] = [
  { text: <strong>All Movie Fan Perks</strong> },
  { text: '1 ticket credit a month' },
  {
    text: <>
      20% Off Concessions (<strong>25% with Movie Club Platinum</strong>){' '}
      <Anchor data-test="link-movie-club-concessions-learn-more"
        dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
        dataLinkName="Movie Club Concessions Learn More"
        href="/movieclub"
        variant="link"
      >
        Learn More
      </Anchor>
    </>,
  },
  { text: 'No online ticket fees' },
  { text: 'Cancel anytime' },
  { text: 'Unused Ticket Credits Roll Over' },
  {
    text: <>
      Member Pricing for Additional Tickets (<strong>Even more tickets with Movie Club Platinum</strong>){' '}
      <Anchor data-test="link-movie-club-tickets-learn-more"
        dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
        dataLinkName="Movie Club Tickets Learn More"
        href="/movieclub"
        variant="link"
      >
        Learn More
      </Anchor>
    </>,
  },
  {
    text: <>
      Access to Premium Screen Upgrades Add-On{' '}
      <Anchor data-test="link-movie-club-upgrades-learn-more"
        dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
        dataLinkName="Movie Club Upgrades Learn More"
        href="/movieclub"
        variant="link"
      >
        Learn More
      </Anchor>
    </>,
  },
  { text: <strong>Starting at $10.99*</strong> },
];

const TierCard = ({
  actionHref,
  actionLabel,
  benefits,
  imageAlt,
  imageSrc,
  starSrc,
  tier,
  title,
}: Readonly<TierCardProps>): React.JSX.Element => {
  const router = useRouter();
  const { trackClick } = useDataAnalytics();

  const handleAction = (): void => {
    trackClick({
      linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
      linkName: `Movie Rewards ${title} ${actionLabel}`,
    });
    router.push(actionHref);
  };

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-sm bg-white"
      data-test={`article-movie-rewards-${tier}-tier`}
    >
      <div className="flex h-[72px] items-center justify-center border-b border-divider px-sm">
        <Image alt={imageAlt}
          className="h-auto w-full max-w-[130px] md:max-w-[150px]"
          height={75}
          src={imageSrc}
          width={295}
        />
      </div>
      <ul className={`flex flex-1 flex-col px-md ${tier === 'movie-fan' ? 'bg-grey-100' : ''}`}
        data-test={`list-movie-rewards-${tier}-tier-benefits`}
      >
        {benefits.map((benefit, index) =>
          <li key={index}
            className="grid grid-cols-[23px_1fr] gap-sm border-b border-divider py-ms last:border-b-0"
          >
            <Image alt=""
              aria-hidden="true"
              height={22}
              src={starSrc}
              width={23}
            />
            <small className="text-card-foreground"
              data-test={`text-movie-rewards-${tier}-benefit-${index + 1}`}
            >
              {benefit.text}
            </small>
          </li>
        )}
      </ul>
      <div className={`flex flex-col items-center gap-ms px-ms py-sm ${tier === 'movie-fan' ? 'bg-grey-100' : ''}`}>
        <Button className="w-full max-w-[210px]"
          data-test={`button-movie-rewards-${tier}-action`}
          onClick={handleAction}
        >
          {actionLabel}
        </Button>
        <Anchor data-test={`link-movie-rewards-${tier}-learn-more`}
          dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
          dataLinkName={`Movie Rewards ${title} Learn More`}
          href={tier === 'movie-fan' ? '/movie-rewards-info/movie-fan' : '/movieclub'}
          variant="link"
        >
          Learn More
        </Anchor>
      </div>
    </article>
  );
};

export const MovieRewardsSelectTier = (): React.JSX.Element => {
  return (
    <section className="relative overflow-hidden bg-primary-darker text-on-primary p-md"
      data-test="section-movie-rewards-select-tier"
    >
      <div aria-hidden="true"
        className={`pointer-events-none absolute inset-0 hidden bg-center bg-no-repeat
          [background-image:url('/img/movie-rewards/background-star.png')] [background-size:auto_100%] md:block`}
      />
      <div className="relative z-1 mx-auto flex max-w-[768px] flex-col items-center gap-ms">
        <Image alt="Cinemark Movie Rewards"
          className="h-auto w-full max-w-[160px]"
          height={87}
          src="/img/movie-rewards/Movie_Rewards_logo.png"
          width={295}
        />
        <h3 className="text-center text-lg"
          data-test="heading-movie-rewards-select-tier"
        >
          Select Your Tier
        </h3>
        <div className="grid w-full grid-cols-1 gap-ms md:grid-cols-2 md:gap-xl">
          <TierCard actionHref="/membership/register?returnurl=/movie-rewards-info"
            actionLabel="Join for Free"
            benefits={movieFanBenefits}
            imageAlt="Cinemark Movie Fan"
            imageSrc="/img/movie-rewards/Movie_Fan_Logo.png"
            starSrc="/img/movie-rewards/gold-star.svg"
            tier="movie-fan"
            title="Movie Fan"
          />
          <TierCard actionHref="/movieclub"
            actionLabel="Upgrade"
            benefits={movieClubBenefits}
            imageAlt="Cinemark Movie Club"
            imageSrc="/img/movie-rewards/Movie_Club_Logo.png"
            starSrc="/img/movie-rewards/star.svg"
            tier="movie-club"
            title="Movie Club"
          />
        </div>
        <small className="max-w-[768px] text-left text-xs"
          data-test="text-movie-rewards-select-tier-disclaimer-details"
        >
          *Plus applicable taxes. Valid on all regular 2D movies. Upcharges may apply for premium formats like XD, 3D, or IMAX. Upcharges may apply in
          Cinemark Reserve Dine-In - 21 and Over theatre in Bellevue, WA and Cinemark Playa Vista and XD in Playa Vista, CA. **Membership or companion
          ticket prices may vary by location, please visit our FAQs for more information.
        </small>
        <small className="text-left"
          data-test="text-movie-rewards-select-tier-disclaimer-terms"
        >
          See the{' '}
          <Anchor className="text-white"
            data-test="link-movie-rewards-terms-conditions"
            dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
            dataLinkName="Movie Rewards Terms and Conditions"
            href="/movie-rewards-info"
            variant="link"
          >
            Movie Rewards Terms and Conditions
          </Anchor>{' '}
          for more information.
        </small>
      </div>
    </section>
  );
};
